/**
 * Renew an expired session without leaving the page, so nothing typed is lost.
 *
 * The app cookie lasts 30 idle minutes, the identity service's session longer. A request
 * that meets an expired cookie waits here while the sign-in runs in its own window; the
 * window lands on `/sitzung-erneuert.html`, which tells this page over a BroadcastChannel
 * (the opener may be severed by the redirects through the identity service) and closes.
 * Then the request is sent once more. A hidden frame would need no click, but the
 * backoffice forbids framing (`X-Frame-Options: DENY`), its own included.
 *
 * A browser may block the window when no click opened it; the notice then offers the
 * button, and the waiting requests wait for it.
 */

import { SIGN_IN_PATH } from "@/lib/api";

const CHANNEL = "countdown-session";
export const RENEWED_PAGE = "/sitzung-erneuert.html";

let renewal: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => {
    listener();
  });
}

/** For `useSyncExternalStore`: whether a renewal is waiting for the sign-in. */
export function subscribeRenewal(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function renewalPending(): boolean {
  return renewal !== null;
}

/** Open the sign-in window. `false` when the browser blocked it. */
export function openSignInWindow(): boolean {
  const url = `${SIGN_IN_PATH}?return_to=${encodeURIComponent(RENEWED_PAGE)}`;
  const opened = window.open(url, "countdown-anmeldung", "popup,width=520,height=700");
  if (opened === null) {
    console.log("[session] sign-in window blocked, waiting for a click");
  }
  return opened !== null;
}

/** Resolves once a sign-in finished in another window. One renewal for all waiting requests. */
export function renewSession(): Promise<void> {
  renewal ??= new Promise<void>((resolve) => {
    const channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = () => {
      channel.close();
      renewal = null;
      notify();
      resolve();
    };
    openSignInWindow();
  });
  notify();
  return renewal;
}
